import type { TConversationBootstrapResponse } from "../types/agent";
import type { TQuickStartResponse } from "../types/quickStart.type";
import type { TTapeSearchParameters } from "../types/tapes.type";
import type { AxiosRequestConfig } from "axios";

import { getEndpoint } from "../endpoints";
import { getLitheBackendStep } from "../utils/litheUtils";
import {
	type TLitheCreationData,
	type TLitheUgFeatureEngineeringResponse,
	type TLitheQuestionApiResponse,
	type TLitheQuestionRequestObject,
	type TLitheQuestionsStepPath,
	type TLitheSearchResponseProps,
} from "../types/lithe.type";

import {
	httpServiceInstance,
	type HttpService,
} from "./http.service";

type TLitheSourceColumnsResponse = {
	source_col_names?: Array<unknown>;
	source_columns?: Array<unknown>;
};

export class LitheService {
	private static http: HttpService;
	private static endpoint: string;
	private static base_url: string;

	constructor() {
		LitheService.http = httpServiceInstance;
		LitheService.endpoint = getEndpoint();
		LitheService.base_url = `${LitheService.endpoint}/api/lithes`;
	}

	private buildQuestionPayload(
		requestObject: TLitheQuestionRequestObject
	): Record<string, any> {
		const payload: Record<string, any> = {
			question_id: requestObject.questionId,
			question_category_id:
				requestObject.questionCategoryId,
			user_input: requestObject.userInput,
			user_input_type: requestObject.userInputType,
		};

		if (requestObject.answerFormat) {
			payload.answer_format = requestObject.answerFormat;
		}

		return payload;
	}

	private logApiResponse<T>(label: string, response: T): T {
		// eslint-disable-next-line no-console
		console.log(
			`[LitheService] ${label} response`,
			response
		);
		return response;
	}

	private async getStepQuestions(
		litheId: string,
		stepPath: string
	): Promise<Array<TLitheQuestionApiResponse>> {
		const url = `${LitheService.base_url}/${litheId}/${stepPath}/questions`;
		const { data } =
			await LitheService.http.get<
				Array<TLitheQuestionApiResponse>
			>(url);
		return this.logApiResponse(
			`GET /${stepPath}/questions`,
			data
		);
	}

	async getQuickStart(
		litheId: string
	): Promise<TQuickStartResponse> {
		return await this.getStepResult(litheId, "quick_start");
	}

	async startQuickStart(
		litheId: string,
		skip = false
	): Promise<TQuickStartResponse> {
		if (!skip)
			return await this.postStepConfirmation(
				litheId,
				"quick_start",
				true
			);
		const { data } =
			await LitheService.http.post<TQuickStartResponse>(
				`${LitheService.base_url}/${litheId}/quick_start/skip`
			);
		return data;
	}

	async reopenStep(
		litheId: string,
		step: string
	): Promise<TQuickStartResponse> {
		const { data } =
			await LitheService.http.post<TQuickStartResponse>(
				`${LitheService.base_url}/${litheId}/reopen`,
				{ data: { step: getLitheBackendStep(step) } }
			);
		return data;
	}

	async getLitheStepQuestions(
		litheId: string,
		stepPath: TLitheQuestionsStepPath
	): Promise<Array<TLitheQuestionApiResponse>> {
		return await this.getStepQuestions(litheId, stepPath);
	}

	private async getStepResult(
		litheId: string,
		stepPath: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/${stepPath}`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse(`GET /${stepPath}`, data);
	}

	private async postStepConfirmation(
		litheId: string,
		stepPath: string,
		isStepConfirmed: boolean
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/${stepPath}`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: isStepConfirmed,
				},
			}
		);
		return this.logApiResponse(`POST /${stepPath}`, data);
	}

	async getLitheConversation(
		litheId: string,
		options?: {
			forceCreate?: boolean;
		}
	): Promise<TConversationBootstrapResponse> {
		const url = `${LitheService.base_url}/${litheId}/conversation`;
		const requestData: Record<string, boolean> = {};
		if (options?.forceCreate) {
			requestData.force_create = true;
		}

		const { data } =
			await LitheService.http.post<TConversationBootstrapResponse>(
				url,
				Object.keys(requestData).length > 0
					? {
							data: requestData,
						}
					: undefined
			);
		return this.logApiResponse("POST /conversation", data);
	}

	async litheSearch(
		request: TTapeSearchParameters,
		config?: AxiosRequestConfig
	): Promise<TLitheSearchResponseProps> {
		const url = `${LitheService.base_url}/search?page=${request.page}`;

		try {
			const { data } =
				await LitheService.http.post<TLitheSearchResponseProps>(
					url,
					{
						data: request.form,
						...config,
					}
				);
			return data;
		} catch (error: any) {
			return {
				lithes: [],
				count: 0,
				error: error.response,
			};
		}
	}

	async createLithe(
		litheCreationData: TLitheCreationData
	): Promise<any> {
		const creationType =
			litheCreationData.litheCreationType;
		const url = `${LitheService.base_url}`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					lithe_name: litheCreationData.litheName,
					asset_class_id: litheCreationData.assetClassId,
					lithe_creation_type: creationType,
					lithe_scope: litheCreationData.litheScope,
				},
			}
		);
		return data;
	}

	async getLithe(litheId: string): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/meta`;
		const { data } = await LitheService.http.get<any>(url);
		return data;
	}

	async getLitheSteps(litheId: string): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/steps`;
		const { data } = await LitheService.http.get<any>(url);
		return data;
	}

	async getLitheCleanDataResult(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/clean_data`;
		const { data } = await LitheService.http.get<any>(url);
		return data;
	}

	async processLitheCleanData(
		litheId: string,
		isStepConfirmed = false
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/clean_data`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: isStepConfirmed,
				},
			}
		);
		return data;
	}

	async getLitheFiltersResult(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/filters`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse("GET /filters", data);
	}

	async processLitheFilters(
		litheId: string,
		isStepConfirmed = false
	): Promise<any> {
		return await this.postStepConfirmation(
			litheId,
			"filters",
			isStepConfirmed
		);
	}

	async getLitheTransDefResult(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/trans_def`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse("GET /trans_def", data);
	}

	async processLitheTransDef(
		litheId: string,
		isStepConfirmed = false
	): Promise<any> {
		return await this.postStepConfirmation(
			litheId,
			"trans_def",
			isStepConfirmed
		);
	}

	async getLitheUgFeatureEngineeringResult(
		litheId: string
	): Promise<TLitheUgFeatureEngineeringResponse> {
		return await this.getStepResult(
			litheId,
			"ug_feature_engg"
		);
	}

	async processLitheUgFeatureEngineering(
		litheId: string,
		isStepConfirmed = false
	): Promise<TLitheUgFeatureEngineeringResponse> {
		return await this.postStepConfirmation(
			litheId,
			"ug_feature_engg",
			isStepConfirmed
		);
	}

	async getLitheUgReplinesResult(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/ug_replines`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse("GET /ug_replines", data);
	}

	async processLitheUgReplines(
		litheId: string,
		isStepConfirmed = false
	): Promise<any> {
		return await this.postStepConfirmation(
			litheId,
			"ug_replines",
			isStepConfirmed
		);
	}

	async getLitheSpecsQuestions(
		litheId: string
	): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/specs/questions`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse(
			"GET /specs/questions",
			data
		);
	}

	async updateLitheSpecsQuestions(
		requestObject: TLitheQuestionRequestObject
	): Promise<any> {
		const url = `${LitheService.base_url}/${requestObject.litheId}/specs/questions`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: this.buildQuestionPayload(requestObject),
			}
		);
		return this.logApiResponse(
			"POST /specs/questions",
			data
		);
	}

	async confirmSpecs(litheId: string): Promise<any> {
		const url = `${LitheService.base_url}/${litheId}/specs`;
		const { data } = await LitheService.http.post<any>(
			url,
			{
				data: {
					is_step_confirmed: true,
				},
			}
		);
		return this.logApiResponse("POST /specs", data);
	}

	async getLitheApprovalInfo(
		litheId: string,
		isUserGuided = false
	): Promise<any> {
		const approvalStep = isUserGuided
			? "ug_approval"
			: "approval";
		const url = `${LitheService.base_url}/${litheId}/${approvalStep}`;
		const { data } = await LitheService.http.get<any>(url);
		return this.logApiResponse(
			`GET /${approvalStep}`,
			data
		);
	}

	async confirmLitheApprovalInfo(
		litheId: string,
		isUserGuided = false
	): Promise<any> {
		const approvalStep = isUserGuided
			? "ug_approval"
			: "approval";
		const url = `${LitheService.base_url}/${litheId}/${approvalStep}`;
		const { data } = await LitheService.http.post<any>(
			url,
			isUserGuided
				? { data: { is_step_confirmed: true } }
				: undefined
		);
		return this.logApiResponse(
			`POST /${approvalStep}`,
			data
		);
	}

	async getLitheUgRFSpecsResults(
		litheId: string
	): Promise<any> {
		return await this.getStepResult(litheId, "ug_rf_specs");
	}

	async processLitheUgRFSpecs(
		litheId: string,
		isStepConfirmed = false
	): Promise<any> {
		return await this.postStepConfirmation(
			litheId,
			"ug_rf_specs",
			isStepConfirmed
		);
	}

	async getSourceColumns(
		litheId: string,
		forStep: "specs" | "approval" | "lithe_approved",
		abortSignal?: AbortSignal
	): Promise<Array<string>> {
		const url = `${LitheService.base_url}/${litheId}/meta/source_columns`;
		const { data } =
			await LitheService.http.post<TLitheSourceColumnsResponse>(
				url,
				{
					data: {
						for_step: forStep,
					},
					signal: abortSignal,
				}
			);
		const rawColumns = Array.isArray(data.source_col_names)
			? data.source_col_names
			: Array.isArray(data.source_columns)
				? data.source_columns
				: [];
		const sourceColumns = rawColumns.filter(
			(column): column is string =>
				typeof column === "string" &&
				column.trim().length > 0
		);
		return this.logApiResponse(
			"POST /meta/source_columns",
			sourceColumns
		);
	}

	async checkForUniqueLitheName(
		litheName: string,
		abortSignal: AbortSignal
	): Promise<any> {
		const url = `${LitheService.base_url}/does_name_exist`;
		const config = {
			data: {
				lithe_name: litheName,
			},
			signal: abortSignal,
		};
		const { data: response } =
			await LitheService.http.post<any>(url, config);
		return response;
	}
}
